import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { InputSwitch } from 'primereact/inputswitch';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { Chip } from 'primereact/chip';
import { Message } from 'primereact/message';
import { Skeleton } from 'primereact/skeleton';
import { Divider } from 'primereact/divider';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { classNames } from 'primereact/utils';
import axiosInstance from '../../config/axios';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const NotificationSettings = () => {
    const toast = useRef(null);

    const userRoles = JSON.parse(localStorage.getItem('userRoles') || '[]');
    const primaryRole = userRoles[0] || 'user';
    const isTenantAdmin = primaryRole === 'tenant_admin';
    const isImpersonating = localStorage.getItem('isImpersonating') === 'true';
    const canAccess = isTenantAdmin || isImpersonating;

    const [loading, setLoading] = useState(false);
    const [savingToggle, setSavingToggle] = useState(false);
    const [savingEmail, setSavingEmail] = useState(false);
    const [fallbackEmail, setFallbackEmail] = useState(null);
    const [savedSettings, setSavedSettings] = useState({ notifications_enabled: true, notifications_email: '' });
    const [emailDraft, setEmailDraft] = useState('');
    const [emailError, setEmailError] = useState(null);

    useEffect(() => {
        document.title = 'Notification Settings - interestng';
    }, []);

    useEffect(() => {
        if (canAccess) loadSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canAccess]);

    const loadSettings = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get('/admin/notification-settings');
            if (response.data.success) {
                const data = response.data.data;
                const state = {
                    notifications_enabled: !!data.notifications_enabled,
                    notifications_email: data.notifications_email || '',
                };
                setSavedSettings(state);
                setEmailDraft(state.notifications_email);
                setFallbackEmail(data.fallback_email || null);
                setEmailError(null);
            }
        } catch (error) {
            console.error('Failed to load notification settings:', error?.message || 'Unknown error');
            toast.current?.show({
                severity: 'error',
                summary: 'Load failed',
                detail: 'Could not load notification settings',
                life: 3000,
            });
        } finally {
            setLoading(false);
        }
    };

    const validateEmail = (value) => {
        if (!value) {
            setEmailError(null);
            return true;
        }
        if (value.length > 191) {
            setEmailError('Email must be 191 characters or fewer.');
            return false;
        }
        if (!EMAIL_REGEX.test(value)) {
            setEmailError('Please enter a valid email address.');
            return false;
        }
        setEmailError(null);
        return true;
    };

    // Shared save routine — optimistic UI with rollback on failure
    const persist = useCallback(async (patch, setSaving, { previous, onSuccessDetail }) => {
        setSaving(true);
        try {
            const response = await axiosInstance.put('/admin/notification-settings', patch);
            if (response.data.success) {
                const data = response.data.data;
                const fresh = {
                    notifications_enabled: !!data.notifications_enabled,
                    notifications_email: data.notifications_email || '',
                };
                setSavedSettings(fresh);
                // Sync draft email back from server (in case backend normalized it)
                setEmailDraft(fresh.notifications_email);
                setFallbackEmail(data.fallback_email || null);
                toast.current?.show({
                    severity: 'success',
                    summary: 'Saved',
                    detail: onSuccessDetail,
                    life: 2000,
                });
                return true;
            }
            throw new Error(response.data.message || 'Save failed');
        } catch (error) {
            // Roll back the UI to previous state
            if (previous) {
                setSavedSettings(previous);
                setEmailDraft(previous.notifications_email);
            }
            const message = error.response?.data?.errors?.notifications_email?.[0]
                || error.response?.data?.message
                || 'Could not save your change';
            toast.current?.show({
                severity: 'error',
                summary: 'Save failed',
                detail: message,
                life: 3500,
            });
            return false;
        } finally {
            setSaving(false);
        }
    }, []);

    const handleToggleChange = (e) => {
        const next = e.value;
        const previous = { ...savedSettings };
        // Optimistic update
        setSavedSettings((s) => ({ ...s, notifications_enabled: next }));
        persist(
            {
                notifications_enabled: next,
                notifications_email: savedSettings.notifications_email || null,
            },
            setSavingToggle,
            {
                previous,
                onSuccessDetail: next ? 'Notifications enabled' : 'Notifications disabled',
            },
        );
    };

    const commitEmailIfChanged = () => {
        const trimmed = emailDraft.trim();
        // No-op if unchanged
        if (trimmed === (savedSettings.notifications_email || '')) return;
        if (!validateEmail(trimmed)) return;

        const previous = { ...savedSettings };
        // Optimistic update
        setSavedSettings((s) => ({ ...s, notifications_email: trimmed }));
        persist(
            {
                notifications_enabled: savedSettings.notifications_enabled,
                notifications_email: trimmed || null,
            },
            setSavingEmail,
            {
                previous,
                onSuccessDetail: trimmed ? 'Recipient updated' : 'Reverted to default admin email',
            },
        );
    };

    const effectiveRecipient = useMemo(() => {
        if (!savedSettings.notifications_enabled) return null;
        return savedSettings.notifications_email || fallbackEmail || null;
    }, [savedSettings, fallbackEmail]);

    const pageHeader = (
        <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-3 mb-4">
            <div>
                <h1 className="text-xl font-semibold text-900 m-0 mb-1">Notification Settings</h1>
                <p className="text-600 text-sm m-0">
                    Control email alerts sent to your organization when users register or purchase credits.
                </p>
            </div>
        </div>
    );

    if (!canAccess) {
        return (
            <>
                <Toast ref={toast} />
                {pageHeader}
                <Card>
                    <div className="flex flex-column align-items-center py-5">
                        <i className="pi pi-lock pi-2x text-400 mb-3" aria-hidden="true" />
                        <h3 className="text-900 font-medium text-base mb-2 m-0">Access Denied</h3>
                        <p className="text-600 text-sm m-0">You need admin privileges to access this page.</p>
                    </div>
                </Card>
            </>
        );
    }

    return (
        <>
            <Toast ref={toast} />
            {pageHeader}

            <Card>
                {loading ? (
                    <div className="flex flex-column gap-3">
                        <Skeleton height="4rem" />
                        <Skeleton height="6rem" />
                        <Skeleton height="4rem" />
                    </div>
                ) : (
                    <>
                        {/* Section 1 — Alert Status */}
                        <div className="flex align-items-center gap-2 mb-3">
                            <i className="pi pi-bell text-primary" aria-hidden="true" />
                            <h2 className="text-base font-medium text-900 m-0">Alert Status</h2>
                        </div>

                        <div className="flex flex-column md:flex-row md:align-items-center md:justify-content-between gap-3">
                            <div className="flex flex-column gap-2 flex-1">
                                <div className="flex align-items-center gap-2 flex-wrap">
                                    <Tag
                                        severity={savedSettings.notifications_enabled ? 'success' : 'secondary'}
                                        value={savedSettings.notifications_enabled ? 'Active' : 'Disabled'}
                                        icon={savedSettings.notifications_enabled ? 'pi pi-check' : 'pi pi-ban'}
                                    />
                                    <span className="text-700 font-medium text-sm">
                                        {savedSettings.notifications_enabled
                                            ? 'Alerts are being sent'
                                            : 'All alerts are turned off'}
                                    </span>
                                </div>
                                <span className="text-500 text-xs">
                                    Changes save automatically.
                                </span>
                            </div>
                            <div className="flex align-items-center justify-content-between gap-2 flex-shrink-0 w-full md:w-auto">
                                <label htmlFor="notifications_enabled" className="text-700 text-sm m-0">
                                    Enable notifications
                                </label>
                                <InputSwitch
                                    inputId="notifications_enabled"
                                    checked={savedSettings.notifications_enabled}
                                    disabled={savingToggle}
                                    onChange={handleToggleChange}
                                />
                            </div>
                        </div>

                        {!savedSettings.notifications_enabled && (
                            <Message
                                severity="warn"
                                text="While disabled, no admin emails will be sent — including critical events like failed payments."
                                className="w-full mt-3"
                            />
                        )}

                        <Divider />

                        {/* Section 2 — Recipient Email */}
                        <div className="flex align-items-center gap-2 mb-3">
                            <i className="pi pi-envelope text-primary" aria-hidden="true" />
                            <h2 className="text-base font-medium text-900 m-0">Recipient Email</h2>
                        </div>

                        <form
                            onSubmit={(e) => { e.preventDefault(); commitEmailIfChanged(); }}
                            noValidate
                            className="field mb-3"
                        >
                            <label htmlFor="notifications_email" className="block font-medium text-sm mb-2">
                                Send alerts to
                            </label>
                            <IconField iconPosition="left" className="w-full">
                                <InputIcon className={savingEmail ? 'pi pi-spin pi-spinner' : 'pi pi-at'} />
                                <InputText
                                    id="notifications_email"
                                    type="email"
                                    inputMode="email"
                                    autoCapitalize="none"
                                    spellCheck={false}
                                    autoComplete="email"
                                    value={emailDraft}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        setEmailDraft(v);
                                        validateEmail(v);
                                    }}
                                    onBlur={commitEmailIfChanged}
                                    placeholder="ops@company.com"
                                    className={classNames('w-full', { 'p-invalid': !!emailError })}
                                    aria-invalid={!!emailError}
                                    aria-describedby="notifications_email_help"
                                    disabled={!savedSettings.notifications_enabled || savingEmail}
                                    maxLength={191}
                                />
                            </IconField>
                            {emailError ? (
                                <small id="notifications_email_help" className="p-error block mt-2" role="alert">
                                    <i className="pi pi-exclamation-circle mr-1" aria-hidden="true" />
                                    {emailError}
                                </small>
                            ) : (
                                <small id="notifications_email_help" className="text-500 block mt-2">
                                    Leave blank to use the default admin email
                                    {fallbackEmail ? ` (${fallbackEmail}).` : '.'} Saves when you click away or press Enter.
                                </small>
                            )}
                        </form>

                        <div className="flex flex-column md:flex-row md:align-items-center gap-2">
                            <span className="text-600 text-sm font-medium">Alerts currently go to:</span>
                            {effectiveRecipient ? (
                                <Chip label={effectiveRecipient} icon="pi pi-send" />
                            ) : (
                                <Tag
                                    severity="secondary"
                                    value="Nobody — notifications are disabled"
                                    icon="pi pi-ban"
                                />
                            )}
                        </div>

                        <Divider />

                        {/* Section 3 — What Gets Notified */}
                        <div className="flex align-items-center gap-2 mb-3">
                            <i className="pi pi-list text-primary" aria-hidden="true" />
                            <h2 className="text-base font-medium text-900 m-0">What Gets Notified</h2>
                        </div>

                        <ul className="list-none p-0 m-0 flex flex-column gap-3">
                            <li className="flex align-items-start gap-3">
                                <i className="pi pi-user-plus text-green-500 text-lg flex-shrink-0 mt-1" aria-hidden="true" />
                                <div className="flex flex-column">
                                    <span className="font-medium text-sm text-900">New user registration</span>
                                    <span className="text-600 text-xs">
                                        Fires when a user signs up via email, MLM referral, company registration, or Google OAuth.
                                    </span>
                                </div>
                            </li>
                            <li className="flex align-items-start gap-3">
                                <i className="pi pi-credit-card text-green-500 text-lg flex-shrink-0 mt-1" aria-hidden="true" />
                                <div className="flex flex-column">
                                    <span className="font-medium text-sm text-900">Credit purchase</span>
                                    <span className="text-600 text-xs">
                                        Fires after a successful Razorpay credit recharge — the invoice PDF is attached.
                                    </span>
                                </div>
                            </li>
                        </ul>
                    </>
                )}
            </Card>
        </>
    );
};

export default NotificationSettings;
